import { pipe } from 'fp-ts/function';
import { useState } from 'react';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { ProductType, useRepositoryQuery } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { LocalizedProperty, lt } from '@mews/framework';
import { ModalSidePanel, SuspenseBoundary, TabbedContent } from '@mews-commander/ui';
import { createActorProfilesMapper } from '@mews-commander/utils';
import { Stack, Tag } from '@optimus-web/core';
import { ProductSidePanelTab } from '../../forms/types';
import { HeaderActions } from './HeaderActions';
import { GeneralTabContent } from './GeneralTabContent';
import { AccountingTabContent } from './AccountingTabContent';
import { PricingTabContent } from './PricingTabContent/PricingTabContent';
import { OptionsTabContent } from './OptionsTabContent';

interface Props {
    closeSidePanel: () => void;
    productId?: string;
    productType?: ProductType;
}

const PRODUCT_SIDE_PANEL_TABS: NonEmptyArray<ProductSidePanelTab> = ['details', 'pricing', 'options', 'accounting'];

export const ProductSidePanel = ({ closeSidePanel, productId, productType }: Props) => {
    const [currentTab, setCurrentTab] = useState<ProductSidePanelTab>('details');
    const currentEnterpriseId = useCurrentEnterpriseId();

    const { data, isFetching } = useRepositoryQuery(
        'products',
        'getAll',
        {
            productIds: [productId ?? ''],
            enterpriseIds: [currentEnterpriseId],
        },
        {
            select: ({ products, profiles }) =>
                products?.[0] ? pipe(products?.[0], createActorProfilesMapper(profiles)) : undefined,
            enabled: !!productId && productType === ProductType.Product,
        }
    );
    return (
        <ModalSidePanel
            content={
                data && productId ? (
                    <Stack vertical spacing="200">
                        <SuspenseBoundary>
                            <TabbedContent
                                key={`product-side-panel-${productId}`}
                                initialTab={currentTab}
                                inline
                                onTabClick={(_, value) => setCurrentTab(value as ProductSidePanelTab)}
                                showDivider
                                tabs={PRODUCT_SIDE_PANEL_TABS}
                                tabLabels={{
                                    details: lt('DetailPlural'),
                                    pricing: lt('Pricing'),
                                    options: lt('Options'),
                                    accounting: lt('Accounting'),
                                }}
                            >
                                {({ matchTabs }) =>
                                    matchTabs({
                                        details: () => (
                                            <GeneralTabContent
                                                createdUtc={data.createdUtc}
                                                creatorProfile={data.creatorProfile}
                                                descriptions={data.descriptions}
                                                externalIdentifier={data.externalIdentifier}
                                                externalNames={data.externalNames}
                                                imageId={data.imageId}
                                                names={data.names}
                                                ordering={data.ordering}
                                                shortNames={data.shortNames}
                                                units={data.units}
                                                updatedUtc={data.updatedUtc}
                                                updaterProfile={data.updaterProfile}
                                            />
                                        ),
                                        pricing: () => <PricingTabContent productId={productId} />,
                                        options: () => <OptionsTabContent productId={productId} />,
                                        accounting: () => <AccountingTabContent productId={productId} />,
                                    })
                                }
                            </TabbedContent>
                        </SuspenseBoundary>
                    </Stack>
                ) : null
            }
            headerActions={
                productId && data ? (
                    <HeaderActions
                        closeSidePanel={closeSidePanel}
                        currentTab={currentTab}
                        productId={productId}
                        productPricing={data.pricing.discriminator}
                        productType={ProductType.Product}
                    />
                ) : null
            }
            loading={isFetching}
            onClose={() => {
                closeSidePanel();
                setCurrentTab('details');
            }}
            opened={!!productId && productType === ProductType.Product}
            title={
                data ? (
                    <Stack itemAlignment="center" spacing="150">
                        <LocalizedProperty property={data.names} />
                        <Tag variant="denim">{lt('Product')}</Tag>
                    </Stack>
                ) : null
            }
            width="medium"
        />
    );
};
